import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../../contexts/AuthContext'
import { isSuperAdmin, isAdmin } from '../../../utils/roleUtils'
import { 
  Home, 
  BarChart2, 
  GraduationCap, 
  Database, 
  Users, 
  History, 
  Activity, 
  User, 
  LogIn, 
  LogOut,
  Layout
} from 'lucide-react'
import './styles.scss'

export default function Sidebar() {
  const { user, isAuthenticated, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [showProfilePopover, setShowProfilePopover] = useState(false)
  const popoverRef = useRef(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setShowProfilePopover(false)
      }
    }
    
    if (showProfilePopover) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showProfilePopover])

  // Hide sidebar for unauthenticated users (after all hooks)
  if (!isAuthenticated) {
    return null
  }

  function handleLogout() {
    logout()
    setShowProfilePopover(false)
    navigate('/')
  }

  // Active link helper
  const isActive = (path) => location.pathname === path ? 'sidebar-item--active' : ''
  const renderIndicator = (path) => location.pathname === path ? <span className="active-indicator-line" /> : null

  return (
    <aside className="sidebar-navigation">
      {/* Top Brand Logo */}
      <div className="sidebar-brand" onClick={() => navigate('/')}>
        <div className="brand-logo-container">
          <img src="/smartadvising.svg" alt="Smart Advising Logo" className="brand-logo-img" />
        </div>
      </div>

      {/* Middle Navigation Icons */}
      <nav className="sidebar-menu">
        <Link to="/" className={`sidebar-item ${isActive('/')}`} data-tooltip="Home">
          {renderIndicator('/')}
          <Home size={22} />
          <span className="sidebar-label">Home</span>
        </Link>

        {isAuthenticated && (
          <>
            <Link to="/workforce-analytics" className={`sidebar-item ${isActive('/workforce-analytics')}`} data-tooltip="Workforce Analytics">
              {renderIndicator('/workforce-analytics')}
              <BarChart2 size={22} />
              <span className="sidebar-label">Workforce</span>
            </Link>

            <Link to="/academic-hub" className={`sidebar-item ${isActive('/academic-hub')}`} data-tooltip="Academic Pathways">
              {renderIndicator('/academic-hub')}
              <GraduationCap size={22} />
              <span className="sidebar-label">Pathways</span>
            </Link>

            {/* Admin Institutional Monitor */}
            {isAdmin(user) && (
              <>
                <Link to="/institutional-executions" className={`sidebar-item ${isActive('/institutional-executions')}`} data-tooltip="Institutional Monitor (Admin)">
                  {renderIndicator('/institutional-executions')}
                  <Activity size={22} />
                  <span className="sidebar-role-badge admin">A</span>
                  <span className="sidebar-label">Monitor</span>
                </Link>

                <Link to="/partner-integration" className={`sidebar-item ${isActive('/partner-integration')}`} data-tooltip="Partner Embed Generator (Admin)">
                  {renderIndicator('/partner-integration')}
                  <Layout size={22} />
                  <span className="sidebar-role-badge admin">A</span>
                  <span className="sidebar-label">Integrations</span>
                </Link>
              </>
            )}

            {/* Super Admin Links */}
            {isSuperAdmin(user) && (
              <div className="super-admin-group">
                <div className="group-divider"></div>
                
                <Link to="/datasets" className={`sidebar-item sidebar-item--admin ${isActive('/datasets')}`} data-tooltip="Datasets Manager (Super Admin)">
                  {renderIndicator('/datasets')}
                  <Database size={20} />
                  <span className="sidebar-role-badge super-admin">SA</span>
                  <span className="sidebar-label">Datasets</span>
                </Link>

                <Link to="/users" className={`sidebar-item sidebar-item--admin ${isActive('/users')}`} data-tooltip="User Management (Super Admin)">
                  {renderIndicator('/users')}
                  <Users size={20} />
                  <span className="sidebar-role-badge super-admin">SA</span>
                  <span className="sidebar-label">Users</span>
                </Link>

                <Link to="/executions" className={`sidebar-item sidebar-item--admin ${isActive('/executions')}`} data-tooltip="Global Executions History (Super Admin)">
                  {renderIndicator('/executions')}
                  <History size={20} />
                  <span className="sidebar-role-badge super-admin">SA</span>
                  <span className="sidebar-label">History</span>
                </Link>
              </div>
            )}
          </>
        )}
      </nav>

      {/* Bottom Profile / Auth Controller */}
      <div className="sidebar-footer">
        {isAuthenticated ? (
          <div className="profile-container" ref={popoverRef}>
            <button 
              className={`profile-avatar-btn ${showProfilePopover ? 'profile-avatar-btn--active' : ''}`}
              onClick={() => setShowProfilePopover(!showProfilePopover)}
              data-tooltip={`Profile: ${user?.username}`}
            >
              <div className="avatar-icon">
                <User size={20} />
              </div>
            </button>

            {/* Profile popover panel */}
            {showProfilePopover && (
              <div className="profile-popover-panel">
                <div className="popover-user-details">
                  <div className="user-icon-large">👤</div>
                  <div className="user-meta">
                    <Link 
                      to="/profile" 
                      className="username-link" 
                      onClick={() => setShowProfilePopover(false)}
                      style={{ textDecoration: 'none', cursor: 'pointer' }}
                    >
                      <span className="username">{user?.username || 'User'}</span>
                    </Link>
                    <span className="user-role-badge">
                      {isSuperAdmin(user) ? 'Super Admin' : isAdmin(user) ? 'Admin' : 'Student'}
                    </span>
                  </div>
                </div>
                
                <div className="popover-actions">
                  <button className="popover-action-btn logout-btn" onClick={handleLogout}>
                    <LogOut size={14} />
                    <span>Log Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <Link to="/login" className="sidebar-item login-item" data-tooltip="Login to Advising">
            <LogIn size={22} />
            <span className="sidebar-label">Login</span>
          </Link>
        )}
      </div>
    </aside>
  )
}
